import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import _isEqual from 'lodash-es/isEqual'

import * as state from '../../../../../../../../../../state'

import * as addLabelOverlay from './add-label-overlay'

@ngCore.Component({
  imports: [
    addLabelOverlay.Component,
    ds.input.number.Component,
    ds.input.select.single.Component,
    ds.input.select.singleEc.Component,
    ds.input.text.Component,
    ds.layout.section.Component,
  ],
  selector: 'atn-adjustment-tab',
  standalone: true,
  styleUrls: ['./adjustment-tab.component.scss'],
  templateUrl: './adjustment-tab.component.html',
})
export class AtnAdjustmentTabComponent {
  /** @see {@link ents.core.common.value.types.Intrinsic} */
  public readonly IntrinsicValueType = ents.core.common.value.types.Intrinsic
  /** @see {@link state.rate.faFunctions.categories.Collection} */
  public readonly idcFunctionCategoryEc = ngCore.inject(
    state.rate.faFunctions.categories.Collection,
  )
  /** @see {@link state.rate.faFunctions.subcategories.Collection} */
  public readonly idcFunctionSubcategoryEc = ngCore.inject(
    state.rate.faFunctions.subcategories.Collection,
  )
  /** @see {@link state.rate.adjustmentLabels.Collection} */
  public readonly adjustmentLabelEc = ngCore.inject(
    state.rate.adjustmentLabels.Collection,
  )
  /** @see {@link state.rate.exclusionLabels.Collection,} */
  public readonly exclusionLabelEc = ngCore.inject(
    state.rate.exclusionLabels.Collection,
  )

  /** @see {@link } */
  public readonly ruleEc = ngCore.inject(state.rate.gl.rules.Collection)

  /**
   * For purpose of ecTarget
   *
   * @see
   * {@link ec.plugins.standard.association.models.Selection.Target}
   */
  public readonly selectionTarget = this.ruleEc.associations?.selectionTarget

  /**
   * The {@link ents.extra.rate.gl.Mapping.Entry | entries} for the editing.
   */
  public readonly entriesSg = ngCore.computed(
    (
      entity = this.ruleEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity()
        .asSignal(),
    ) => entity?.mapping?.entries ?? [],
    { equal: _isEqual },
  )

  /**
   * Total percentage in range of 0 - 100 of all {@link entriesSg}.
   */
  public readonly entriesTotalPercentSg = ngCore.computed(
    (entries = this.entriesSg()) =>
      Math.round(entries.reduce((acc, entry) => acc + entry.percent, 0) * 100),
  )

  /**
   * Boolean representing if the total percentage of all {@link entriesSg}
   * is equal to 100%.
   */
  public readonly areEntriesTotal100Sg = ngCore.computed(
    (inProgressEntriesTotalPercent = this.entriesTotalPercentSg()) =>
      inProgressEntriesTotalPercent === 100,
  )

  /**
   * Each {@link ents.extra.rate.gl.Mapping.Entry} is associated with a
   * {@link ec.plugins.standard.association.models.Selection.Target} that is used
   * for selection.
   */
  public readonly entrySelectionTargetLookupSg = ngCore.computed(
    (entries = this.entriesSg()) =>
      entries.map((_, idx) =>
        this.ruleEc.associations?.selectionTarget.forEntry(idx),
      ),
  )

  /** Idx of the row that is being hovered */
  public rowHoveringIdx: number | undefined

  /**
   * Mainly used to get the
   * {@link state.rate.gl.common.DEFERRED_EXCLUSION_CATEGORY_ID} option, when
   * relevant
   */
  public getPrependedIdcCategoryItems(
    entry: ents.extra.rate.gl.Mapping.Entry,
  ): readonly ds.input.select.models.Item[] {
    return entry.exclusionLabel
      ? [
          {
            label: state.rate.gl.common.DEFERRED_EXCLUSION_CATEGORY_LABEL,
            value: state.rate.gl.common.DEFERRED_EXCLUSION_CATEGORY_ID,
          },
        ]
      : []
  }

  /**
   * TODO:
   */
  public getIdcSubcategoryGroupId(
    entry: ents.extra.rate.gl.Mapping.Entry,
  ): ec.plugins.standard.group.models.Group.Id | undefined {
    return ents.extra.common.FaFunction.Categorization.Ids.from(
      entry.faFunctionCategorization,
    )?.categoryId
  }

  public addEntryHandler = () =>
    this.ruleEc.plugins.editingPlugin.feature.actions.edit
      .from(
        'EditSlideout.Tabs.AdjustmentTab.Sections.Mapping.EntityTable.Header.Add.Button.Clicked',
      )
      .dispatch({
        edit: entity => {
          if (!entity) {
            return
          }

          entity.mapping ??= {
            entries: [],
          }
          entity.mapping.entries.push(
            _generateDefaultMappingEntry(), // FIXME:
          )
        },
      })

  public percentChangeHandler = (idx: number, value: number | undefined) =>
    this.ruleEc.plugins.editingPlugin.feature.actions.edit
      .from(
        'EditSlideout.Tabs.AdjustmentTab.Sections.Mapping.EntityTable.Row-${idx}.Controls.PercentControl.Changed',
      )
      .dispatch({
        edit: entity => {
          if (!entity) {
            return
          }

          entity.mapping ??= {
            entries: [],
          }
          entity.mapping.entries[idx].percent = value ?? 0
        },
      })

  public updateEntryExclusionLabel = (exclusionLabel: string, idx: number) =>
    this.ruleEc.plugins.editingPlugin.feature.actions.edit
      .from(
        `EditSlideout.Tabs.AdjustmentTab.Sections.Mapping.EntityTable.Row-${idx}.Controls.ExclusionLabel.Changed`,
      )
      .dispatch({
        edit: entity => {
          if (!entity) {
            return
          }

          entity.mapping ??= {
            entries: [],
          }
          entity.mapping.entries[idx].exclusionLabel = exclusionLabel
        },
      })

  public removeEntryHandler = (idx: number) =>
    this.ruleEc.plugins.editingPlugin.feature.actions.edit
      .from(
        `EditSlideout.Tabs.AdjustmentTab.Sections.Mapping.EntityTable.Row-${idx}.Delete.Button.Clicked`,
      )
      .dispatch({
        edit: entity => {
          if (!entity) {
            return
          }

          entity.mapping ??= {
            entries: [],
          }
          entity.mapping.entries = entity.mapping.entries.filter(
            (_, index) => index !== idx,
          )
        },
      })
}

// Needed for default entry for General ledger and Rule adjustment tab
function _generateDefaultMappingEntry(): ents.extra.rate.gl.Mapping.Entry {
  return {
    percent: 1, // = 100%
  }
}
